/**
 * <s-page-size> — Splits a server-rendered product grid into client-side pages so
 * shoppers can choose how many products to see at once (e.g. 20, 50, 100 or all).
 *
 * The server renders every product it can in one paginate window; this element hides
 * the items outside the current page and renders numbered page controls.
 *
 * Attributes (on <s-page-size>):
 *   param            — URL param holding the page size, shared with the page-size <select>
 *                      that <s-live-form> serializes (default "per_page").
 *   default-size     — Page size used when the URL has no valid value (default 20).
 *   data-label       — Accessible name for the page nav.
 *   data-previous    — Accessible label for the previous-page control.
 *   data-next        — Accessible label for the next-page control.
 *   data-page        — Label template for a page control; "[number]" is replaced.
 *   data-current     — Label template for the current page; "[number]" is replaced.
 *
 * Required child markup:
 *   [data-page-size-items] — List whose element children are the paged items.
 *   [data-page-size-nav]   — Empty element that receives the generated page controls.
 *
 * External coupling:
 *   history.replaceState — Keeps the current page in the `product_page` URL param.
 *   Closest <s-live-form> form submit — Resets to page 1 when filters, sort or size change.
 */
const PAGE_PARAM = 'product_page';

const LINK_CLASS =
  'inline-flex items-center justify-center min-w-touch-target min-h-touch-target rounded text-sm hover:bg-surface-secondary motion-safe:transition-[color,background-color,transform] motion-safe:duration-150 motion-safe:active:scale-[0.95]';
const DISABLED_CLASS =
  'inline-flex items-center justify-center min-w-touch-target min-h-touch-target rounded text-sm text-on-surface-secondary opacity-40 cursor-not-allowed';
const CURRENT_CLASS =
  'inline-flex items-center justify-center min-w-touch-target min-h-touch-target rounded text-sm bg-interactive text-interactive-text font-medium';

class PageSize extends HTMLElement {
  #ac;
  #page = 1;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const form = this.closest('s-live-form')?.querySelector('form');
    form?.addEventListener('submit', () => this.#setPageParam(1), opts);

    const params = new URLSearchParams(window.location.search);
    this.#page = Math.max(1, parseInt(params.get(PAGE_PARAM), 10) || 1);
    this.#render(false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #size() {
    const param = this.getAttribute('param') || 'per_page';
    const value = new URLSearchParams(window.location.search).get(param);
    if (value === 'all') return Infinity;
    const size = parseInt(value, 10);
    if (size > 0) return size;
    return parseInt(this.getAttribute('default-size'), 10) || 20;
  }

  get #items() {
    const list = this.querySelector('[data-page-size-items]');
    return list ? Array.from(list.children) : [];
  }

  #setPageParam(page) {
    const url = new URL(window.location.href);
    if (page > 1) url.searchParams.set(PAGE_PARAM, String(page));
    else url.searchParams.delete(PAGE_PARAM);
    history.replaceState(history.state, '', url);
  }

  #goTo(page) {
    this.#page = page;
    this.#setPageParam(page);
    this.#render(true);
  }

  #render(scroll) {
    const items = this.#items;
    const size = this.#size;
    const pages = size === Infinity ? 1 : Math.max(1, Math.ceil(items.length / size));
    this.#page = Math.min(this.#page, pages);

    const start = size === Infinity ? 0 : (this.#page - 1) * size;
    const end = size === Infinity ? items.length : start + size;
    items.forEach((item, index) => {
      item.hidden = index < start || index >= end;
    });

    this.#renderNav(pages);

    if (scroll) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }
  }

  #label(template, number) {
    return (template || '').replace('[number]', String(number));
  }

  #control(label, html, page, enabled) {
    if (!enabled) {
      const span = document.createElement('span');
      span.className = DISABLED_CLASS;
      span.setAttribute('aria-disabled', 'true');
      span.setAttribute('aria-label', label);
      span.innerHTML = html;
      return span;
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.className = LINK_CLASS;
    button.setAttribute('aria-label', label);
    button.innerHTML = html;
    button.addEventListener('click', () => this.#goTo(page));
    return button;
  }

  #renderNav(pages) {
    const host = this.querySelector('[data-page-size-nav]');
    if (!host) return;
    host.replaceChildren();
    if (pages <= 1) return;

    const nav = document.createElement('nav');
    nav.className = 'flex items-center justify-center gap-1';
    nav.setAttribute('aria-label', this.dataset.label || '');

    const prevIcon = this.querySelector('[data-page-size-prev-icon]')?.innerHTML || '&lsaquo;';
    const nextIcon = this.querySelector('[data-page-size-next-icon]')?.innerHTML || '&rsaquo;';

    nav.append(this.#control(this.dataset.previous || '', prevIcon, this.#page - 1, this.#page > 1));

    for (let page = 1; page <= pages; page++) {
      if (page === this.#page) {
        const current = document.createElement('span');
        current.className = CURRENT_CLASS;
        current.setAttribute('aria-current', 'page');
        const sr = document.createElement('span');
        sr.className = 'sr-only';
        sr.textContent = this.#label(this.dataset.current, page);
        const visible = document.createElement('span');
        visible.setAttribute('aria-hidden', 'true');
        visible.textContent = String(page);
        current.append(sr, visible);
        nav.append(current);
      } else {
        const button = this.#control(this.#label(this.dataset.page, page), '', page, true);
        button.textContent = String(page);
        nav.append(button);
      }
    }

    nav.append(this.#control(this.dataset.next || '', nextIcon, this.#page + 1, this.#page < pages));
    host.append(nav);
  }
}

if (!customElements.get('s-page-size')) customElements.define('s-page-size', PageSize);
